import { $on, $trigger, YChangeEvent, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $form, YInput, YInputOptions } from '../../y/form';
import { UIViewSpec } from '../view';

export interface UIAdapterObserver {
    onItemAdded(index: number, adapter: UIAdapter<any>): void;
    onItemRemoved(index: number, adapter: UIAdapter<any>): void;
    onItemUpdated(index: number, adapter: UIAdapter<any>): void;
    onItemMoved(from: number, to: number, adapter: UIAdapter<any>): void;
    onItemsChanged(adapter: UIAdapter<any>): void;
}

export abstract class UIAdapter<View extends UIViewSpec = UIViewSpec> {
    section: number;

    private _attached?: UIAdapterObserver
    private _observers: UIAdapterObserver[];
    private _header?: UIViewSpec | (() => UIViewSpec);

    constructor() {
        this._observers = [];
        this.section = -1;
    }

    detach(o: UIAdapterObserver) {
        if(o == this._attached)
            this._observers.shift();
    }

    attach(o: UIAdapterObserver, section: number = -1) {
        this.section = section;
        if(this._attached)
            this._observers.shift();
        this._attached = o;
        this._observers.unshift(o);
    }

    addObserver(o: UIAdapterObserver) {
        this._observers.push(o);
    }

    onViewMoved(_from: number, _to: number) {

    }

    set header(header: UIViewSpec | (() => UIViewSpec) | undefined) {
        this._header = header;
    }

    protected notifyItemAdded(i: number) {
        for(const o of this._observers) {
            o.onItemAdded(i, this);
        }
    }

    protected notifyItemRemoved(i: number) {
        for(const o of this._observers) {
            o.onItemRemoved(i, this);
        }
    }

    protected notifyItemUpdated(i: number) {
        for(const o of this._observers) {
            o.onItemUpdated(i, this);
        }
    }

    protected notifyItemMoved(from: number, to: number) {
        for(const o of this._observers) {
            o.onItemMoved(from, to, this);
        }
    }

    protected notifyItemsChanged() {
        for(const o of this._observers) {
            o.onItemsChanged(this);
        }
    }

    getHeader(): UIViewSpec | undefined {
        const header = this._header;

        if(this.count && header) {
            if(typeof header === 'function') {
                return header();
            }

            return header;
        }

        return undefined;
    }

    abstract getView(i: number): View;
    abstract count: number;
}

export class UIViewAdapter<View extends UIViewSpec = UIViewSpec> extends UIAdapter<View> {
    private _view: View;

    constructor(view: View) {
        super();
        this._view = view;
    }

    get count() {
        return 1;
    }

    getView() {
        return this._view;
    }
}

export class UIArrayAdapter<View extends UIViewSpec = UIViewSpec> extends UIAdapter<View> {
    private _views: (() => View)[];

    constructor(views: (() => View)[]) {
        super();

        this._views = views;
    }

    getView(i: number) {
        return this._views[i]();
    }

    get count() {
        return this._views.length;
    }
}

export interface UIAdapterEntry<Model, View, Input> {
    model: Model;
    view?: View;
    input?: Input;
    extra?: any;
}

export class UIDataAdapter<Model, View extends UIViewSpec = UIViewSpec, Input extends YInput<Model> = YInput<Model>> extends UIAdapter<View> {
    private _entries!: UIAdapterEntry<Model, View, Input>[];
    private _generator: (model: Model, entry: UIAdapterEntry<Model, View, Input>, index: number) => View;

    constructor(generator: (model: Model, entry: UIAdapterEntry<Model, View, Input>, index: number) => View, data?: Model[]) {
        super();

        this.data = data;
        this._generator = generator;
    }

    set data(data: Model[] | undefined) {
        this._entries = data ? data.map(m => ({ model: m })) : [];
        this.notifyItemsChanged();
    }

    get data(): Model[] {
        return this._entries.map(e => e.model);
    }

    get views() {
        const views: View[] = [];
        for(const e of this._entries) {
            if(e.view)
                views.push(e.view);
        }
        return views;
    }

    push(m: Model) {
        this._entries.push({ model: m });
        this.notifyItemAdded(this._entries.length - 1);
    }

    removeAt(index: number) {
        const entries = this._entries;
        if(index < 0 || index >= entries.length) return false;

        entries.splice(index, 1);
        this.notifyItemRemoved(index);

        return true;
    }

    remove(e: UIAdapterEntry<Model, View, Input>) {
        return this.removeAt(this._entries.indexOf(e));
    }

    getView(i: number) {
        const entry = this._entries[i];
        if(!entry.view)
            entry.view = this._generator(entry.model, entry, i);
        return entry.view;
    }

    get count() {
        return this._entries.length;
    }
}

interface UIInputAdapterItem<View, Input> {
    view: View;
    input?: Input;
    extra?: any;
}

export class UIInputAdapter<Model, View extends UIViewSpec = UIViewSpec, Input extends YInput<Model> = YInput<Model>> extends UIAdapter<View> implements YInput<Model[]> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Model[]> & YChangeEvent>;

    private _entries: UIAdapterEntry<Model, View, Input>[];
    private _maker: (m: Model, entry: UIAdapterEntry<Model, View, Input>) => UIInputAdapterItem<View, Input>;
    private _required?: boolean;

    okay: boolean;
    _dirty!: boolean;
    disabled!: boolean;

    constructor(maker: (m: Model, entry: UIAdapterEntry<Model, View, Input>) => UIInputAdapterItem<View, Input>, options: YInputOptions = {}) {
        super();

        this._maker = maker;
        this._entries = [];
        this._required = options.required;

        this.okay = !options.required;
    }

    private _makeItem(entry: UIAdapterEntry<Model, View, Input>) {
        const item = this._maker(entry.model, entry);
        if(item.input) {
            item.input.value = entry.model;
            $on(item.input, 'state', () => { this._recalc() });
        }
        return item;
    }

    push(m: Model, focus?: boolean) {
        $form.setDirty(this, true);

        const entry: UIAdapterEntry<Model, View, Input> = { model: m };
        this._entries.push(entry);

        this.notifyItemAdded(this._entries.length - 1);
        this._recalc();

        if(focus && entry.input && entry.input.focus)
            entry.input.focus();

        $trigger(this, 'change');

        return entry;
    }

    unshift(m: Model, focus?: boolean) {
        $form.setDirty(this, true);

        const entry: UIAdapterEntry<Model, View, Input> = { model: m };
        this._entries.unshift(entry);

        this.notifyItemAdded(0);
        this._recalc();

        if(focus && entry.input && entry.input.focus)
            entry.input.focus();

        $trigger(this, 'change');

        return entry;
    }

    insertBefore(m: Model, before: UIAdapterEntry<Model, View, Input> | undefined, focus?: boolean) {
        if(!before) return this.push(m, focus);

        $form.setDirty(this, true);

        const pos = this._entries.indexOf(before);
        if(pos == -1) return;

        const entry: UIAdapterEntry<Model, View, Input> = { model: m };
        this._entries.splice(pos, 0, entry);

        this.notifyItemAdded(pos);
        this._recalc();

        if(focus && entry.input && entry.input.focus)
            entry.input.focus();

        $trigger(this, 'change');

        return entry;
    }

    insertAfter(m: Model, after: UIAdapterEntry<Model, View, Input> | undefined, focus?: boolean) {
        if(!after) return this.unshift(m, focus);

        $form.setDirty(this, true);

        const pos = this._entries.indexOf(after);
        if(pos == -1) return;

        const entry: UIAdapterEntry<Model, View, Input> = { model: m };
        this._entries.splice(pos + 1, 0, entry);

        this.notifyItemAdded(pos + 1);
        this._recalc();

        if(focus && entry.input && entry.input.focus)
            entry.input.focus();

        $trigger(this, 'change');

        return entry;
    }

    removeAt(index: number) {
        const entries = this._entries;
        if(index < 0 || index >= entries.length) return false;

        entries.splice(index, 1);

        $form.setDirty(this, true);
        this.notifyItemRemoved(index);

        this._recalc();

        $trigger(this, 'change');

        return true;
    }

    remove(e: UIAdapterEntry<Model, View, Input>) {
        return this.removeAt(this._entries.indexOf(e));
    }

    removeIf(predicate: (v: Model) => boolean) {
        return this.removeAt(this._entries.findIndex(e => predicate(e.model)));
    }

    updateIf(predicate: (v: Model) => boolean, model: Model) {
        const index = this._entries.findIndex(e => predicate(e.model));
        if(index < 0) return false;

        const entry = this._entries[index];
        entry.model = model;
        entry.view = undefined;
        entry.input = undefined;

        this.notifyItemUpdated(index);

        this._recalc();

        $trigger(this, 'change');

        return true;
    }

    updateAll() {
        this.value = this.value;
    }

    override onViewMoved(from: number, to: number) {
        if(from == to) return;

        const entries = this._entries;
        entries.splice(to, 0, entries.splice(from, 1)[0]);

        this.notifyItemMoved(from, to);

        $trigger(this, 'change');
    }

    get value(): Model[] {
        const entries = this._entries;

        for(const e of entries) {
            if(e.input)
                e.model = e.input.value;
        }

        return entries.map(e => e.model);
    }

    set value(models: Model[] | undefined) {
        this._entries = models ? models.map(m => ({ model: m })) : [];
        this.notifyItemsChanged();
        this._recalc();

        $trigger(this, 'change');
    }

    get views(): View[] {
        return this._entries.map(e => e.view).filter(v => v) as View[];
    }

    get entries() {
        return this._entries;
    }

    get inputs(): (Input | undefined)[] {
        return this._entries.map(e => e.input);
    }

    get count() {
        return this._entries.length;
    }

    private _recalc() {
        let changed = false;

        const okay = this._entries.length ? this._entries.every(i => !i.input || i.input.okay) : !this._required;

        if(okay !== this.okay) {
            changed = true;
            this.okay = okay;
        }

        const dirty = this._dirty || this._entries.some(i => !!i.input && i.input.dirty);

        if(dirty !== this._dirty) {
            changed = true;
            this._dirty = dirty;
        }

        if(changed) {
            $trigger(this, 'state');
        }
    }

    isEmpty() {
        return !this._entries.length
    }

    get dirty() {
        return this._dirty;
    }

    set dirty(d: boolean) {
        if(d === this._dirty) return;
        this._dirty = d;
        if(!d) {
            for(const i of this._entries) {
                if(i.input)
                    i.input.dirty = false;
            }
        }
    }

    markInvalid(focus: boolean): boolean {
        let focused = false;
        for(const i of this._entries) {
            if(i.input && i.input.markInvalid)
                focused = i.input.markInvalid(focus && !focused) || focused;
        }
        return focused;
    }

    focus() {
        for(const i of this._entries) {
            if(i.input && i.input.focus) {
                i.input.focus();
                return;
            }
        }
    }

    getView(i: number) {
        let entry = this._entries[i];

        if(!entry.view) {
            const item = this._makeItem(entry);
            entry.view = item.view;
            entry.input = item.input;
            entry.extra = item.extra;
        }

        return entry.view;
    }

    findView(view: View) {
        return this._entries.find(e => e.view == view);
    }
}
